/**
 * repositories/communicationsRepository.ts
 * Communications table data access layer
 *
 * Architecture: WU1.5 Task 9 - Communication Repository Pattern
 *
 * IMPORTANT PATTERNS:
 * 1. templateSnapshot JSONB field keeps snake_case internally (template_type, variables_used)
 *    - This is intentional per CODING_STANDARDS JSONB allowlist pattern
 *    - DO NOT deep-camelize this field - it's contractual audit evidence
 *    - See: docs/development/CODING_STANDARDS.md#repository-pattern
 *
 * 2. Communications are audit-ish records with immutability enforcement
 *    - Identity fields (entityType, entityId, recipientEmail, templateSnapshot) NEVER modified
 *    - Only delivery lifecycle fields can be updated (status, deliveredAt, etc.)
 *    - See: CommunicationDeliveryUpdate type for enforced restrictions
 *
 * 3. Default ordering: sent_at DESC (most recent first)
 *    - Consistent across all query methods
 *    - Pagination shape: { items, total } matching other repos
 */

import { supabase } from '@/lib/supabase';
import { toDomain, toWire, toPatch } from '@/lib/supabaseMappers';
import type { Database } from '@/types/supabase.types';
import type {
  Communication,
  CommunicationInsert,
  CommunicationUpdate,
  CommunicationEntityType
} from '@/types/supabase/Communication';

// ============================================================================
// WIRE TYPES (snake_case from database)
// ============================================================================

type CommunicationInsertRow = Database['public']['Tables']['communications']['Insert'];
type CommunicationUpdateRow = Database['public']['Tables']['communications']['Update'];

/**
 * Raw shape for JOINed email_templates data
 * Used when querying communications with template metadata
 */
interface RawTemplateJoin {
  id: string;
  template_type: string;
  subject: string;
}

/**
 * Raw shape for communication rows with JOINed template
 */
type RawCommunicationWithTemplate = Database['public']['Tables']['communications']['Row'] & {
  email_templates: RawTemplateJoin | null;
};

/**
 * Raw shape for template_snapshot JSONB field
 * IMPORTANT: This stays in snake_case per JSONB allowlist pattern (CODING_STANDARDS.md)
 */
interface RawTemplateSnapshot {
  template_type?: string;
  [key: string]: unknown; // Other snapshot fields vary by template type
}

// ============================================================================
// PROJECTION CONSTANTS
// ============================================================================

/**
 * Standard communication projection
 * NOTE: If you add a column to `communications`, update this list AND the Communication type.
 */
const COMM_PROJECTION = 'id,entity_type,entity_id,recipient_email,template_id,template_snapshot,sent_by,status,message_id,sent_at,delivered_at,bounced_at,failed_at,complained_at,last_event_at,provider,provider_message_id,error_code,error_message,created_at,updated_at' as const;

/**
 * Email template JOIN projection (subset for display metadata)
 */
const TEMPLATE_JOIN_PROJECTION = 'id,template_type,subject' as const;

// ============================================================================
// RETURN TYPES
// ============================================================================

/**
 * Communication with joined email template metadata
 *
 * IMPORTANT: The joined 'template' is current metadata at query time,
 * NOT guaranteed to match what was actually sent.
 *
 * Source of truth for sent content: communication.templateSnapshot
 * Use case for joined template: Display current template name/subject in admin UI
 */
export interface CommunicationWithTemplate {
  communication: Communication;
  template: {
    id: string;
    templateType: string;
    subject: string;
  } | null;
}

/**
 * Communication statistics for reporting
 */
export interface CommunicationStats {
  total: number;
  sent: number;
  failed: number;
  delivered: number;
  byTemplateType: Record<string, number>;
}

/**
 * Invitation-specific communication statistics
 *
 * Filter criteria: template_type IN ['invitation', 'activation-reminder']
 * deliveryRate: 0-100 percentage (delivered / sent * 100)
 */
export interface InvitationStats {
  totalInvitationsSent: number;
  totalRemindersSent: number;
  failedInvitations: number;
  failedReminders: number;
  deliveryRate: number; // 0-100 percentage
}

/**
 * Restricted update type - only delivery lifecycle fields
 *
 * This enforces immutability for audit integrity.
 * Identity fields (entityType, entityId, recipientEmail, templateSnapshot)
 * cannot be modified after creation.
 */
export type CommunicationDeliveryUpdate = Pick<
  CommunicationUpdate,
  | 'status'
  | 'sentAt'
  | 'deliveredAt'
  | 'bouncedAt'
  | 'failedAt'
  | 'complainedAt'
  | 'lastEventAt'
  | 'errorCode'
  | 'errorMessage'
  | 'providerMessageId'
>;

/**
 * List options for pagination
 */
export interface ListCommunicationsOptions {
  offset?: number;
  limit?: number;
  withTotal?: boolean;
}

/**
 * Paginated list result
 */
export interface CommunicationListResult {
  items: Communication[];
  total?: number;
}

// ============================================================================
// REPOSITORY METHODS
// ============================================================================

/**
 * Create a new communication record
 *
 * @param data - Communication data (camelCase)
 * @returns Created communication (camelCase)
 */
export async function create(data: CommunicationInsert): Promise<Communication> {
  const wireData = toWire<CommunicationInsertRow>(data);

  const { data: row, error } = await supabase
    .from('communications')
    .insert(wireData)
    .select(COMM_PROJECTION)
    .single();

  if (error) {
    throw new Error(`Failed to create communication: ${error.message}`);
  }

  if (!row) {
    throw new Error('Failed to create communication: No data returned');
  }

  return toDomain<Communication>(row);
}

/**
 * Update communication delivery lifecycle fields only
 *
 * IMMUTABILITY: This method ONLY accepts delivery lifecycle updates.
 * Identity fields (entityType, entityId, recipientEmail, templateSnapshot)
 * cannot be modified after creation - they are audit evidence.
 *
 * Allowed fields: status, sentAt, deliveredAt, bouncedAt, failedAt,
 * complainedAt, lastEventAt, errorCode, errorMessage, providerMessageId
 *
 * @param id - Communication ID
 * @param updates - Delivery lifecycle updates (camelCase)
 * @returns Updated communication (camelCase)
 */
export async function update(
  id: string,
  updates: CommunicationDeliveryUpdate
): Promise<Communication> {
  const wireUpdates = toPatch<CommunicationUpdateRow>(updates);

  const { data: row, error } = await supabase
    .from('communications')
    .update(wireUpdates)
    .eq('id', id)
    .select(COMM_PROJECTION)
    .single();

  if (error) {
    throw new Error(`Failed to update communication: ${error.message}`);
  }

  if (!row) {
    throw new Error('Communication not found or update failed');
  }

  return toDomain<Communication>(row);
}

/**
 * Get communication by ID
 *
 * @param id - Communication ID
 * @returns Communication or null if not found
 */
export async function getById(id: string): Promise<Communication | null> {
  const { data: row, error } = await supabase
    .from('communications')
    .select(COMM_PROJECTION)
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // Not found
    }
    throw new Error(`Failed to get communication: ${error.message}`);
  }

  return row ? toDomain<Communication>(row) : null;
}

/**
 * Get all communications for a specific entity
 *
 * Default ordering: sent_at DESC (most recent first)
 *
 * @param entityType - Type of entity
 * @param entityId - ID of the entity
 * @returns Array of communications (camelCase)
 */
export async function getByEntity(
  entityType: CommunicationEntityType,
  entityId: string
): Promise<Communication[]> {
  const { data: rows, error } = await supabase
    .from('communications')
    .select(COMM_PROJECTION)
    .eq('entity_type', entityType)
    .eq('entity_id', entityId)
    .order('sent_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to get communications for ${entityType}:${entityId}: ${error.message}`);
  }

  return (rows || []).map(row => toDomain<Communication>(row));
}

/**
 * Check if a specific email type has been sent to an entity
 *
 * ⚠️ SECURITY: This prevents duplicate email sends to real users.
 * Changing this logic can cause double-sends (e.g., multiple approval emails).
 *
 * Keying: entityType + entityId + templateType (all required)
 *
 * @param params - Entity and template type filter
 * @returns True if email has been sent
 */
export async function hasEmailBeenSent({
  entityType,
  entityId,
  templateType
}: {
  entityType: CommunicationEntityType;
  entityId: string;
  templateType: string;
}): Promise<boolean> {
  const { data: rows, error } = await supabase
    .from('communications')
    .select('id')
    .eq('entity_type', entityType)
    .eq('entity_id', entityId)
    .eq('status', 'sent')
    .contains('template_snapshot', { template_type: templateType })
    .limit(1);

  if (error) {
    throw new Error(`Failed to check email status: ${error.message}`);
  }

  return (rows?.length || 0) > 0;
}

/**
 * List communications with pagination
 *
 * Default ordering: sent_at DESC (most recent first)
 *
 * @param options - Pagination options
 * @returns Paginated list result
 */
export async function list(
  options: ListCommunicationsOptions = {}
): Promise<CommunicationListResult> {
  const { offset = 0, limit = 50, withTotal = false } = options;

  const selectOptions = withTotal ? { count: 'exact' as const } : undefined;

  let query = supabase
    .from('communications')
    .select(COMM_PROJECTION, selectOptions)
    .order('sent_at', { ascending: false })
    .range(offset, offset + limit - 1);

  const { data: rows, error, count } = await query;

  if (error) {
    throw new Error(`Failed to list communications: ${error.message}`);
  }

  const items = (rows || []).map(row => toDomain<Communication>(row));

  return {
    items,
    ...(withTotal && count !== null && { total: count })
  };
}

/**
 * Get communications with current email template metadata
 *
 * IMPORTANT: The joined 'template' is current metadata at query time,
 * NOT guaranteed to match what was actually sent.
 *
 * Source of truth for sent content: communication.templateSnapshot
 * Use case for joined template: Display current template name/subject in admin UI
 *
 * @param entityType - Type of entity
 * @param entityId - ID of the entity
 * @returns Communications with template metadata
 */
export async function getByEntityWithTemplates(
  entityType: CommunicationEntityType,
  entityId: string
): Promise<CommunicationWithTemplate[]> {
  const { data: rows, error } = await supabase
    .from('communications')
    .select(`
      ${COMM_PROJECTION},
      email_templates (${TEMPLATE_JOIN_PROJECTION})
    `)
    .eq('entity_type', entityType)
    .eq('entity_id', entityId)
    .order('sent_at', { ascending: false });

  if (error) {
    throw new Error(`Failed to get communications with templates: ${error.message}`);
  }

  return (rows || []).map(rawRow => {
    // JOIN MAPPER (local) - email_templates subset
    const row = rawRow as RawCommunicationWithTemplate;
    const templateRaw = row.email_templates;
    const template = templateRaw ? {
      id: templateRaw.id,
      templateType: templateRaw.template_type,
      subject: templateRaw.subject
    } : null;

    return {
      communication: toDomain<Communication>(row),
      template
    };
  });
}

/**
 * Get communication statistics for reporting
 *
 * Computes aggregations in database for efficiency.
 *
 * @param entityType - Optional entity type filter
 * @returns Communication statistics
 */
export async function getStats(
  entityType?: CommunicationEntityType
): Promise<CommunicationStats> {
  let query = supabase
    .from('communications')
    .select('status, template_snapshot');

  if (entityType) {
    query = query.eq('entity_type', entityType);
  }

  const { data: rows, error } = await query;

  if (error) {
    throw new Error(`Failed to get communication stats: ${error.message}`);
  }

  const stats: CommunicationStats = {
    total: rows?.length || 0,
    sent: 0,
    failed: 0,
    delivered: 0,
    byTemplateType: {}
  };

  rows?.forEach(row => {
    // Count by status
    if (row.status === 'sent') stats.sent++;
    else if (row.status === 'failed') stats.failed++;
    else if (row.status === 'delivered') stats.delivered++;

    // Count by template type (JSONB field stays in snake_case per CODING_STANDARDS)
    const snapshot = row.template_snapshot as RawTemplateSnapshot | null;
    const templateType = snapshot?.template_type || 'unknown';
    stats.byTemplateType[templateType] = (stats.byTemplateType[templateType] || 0) + 1;
  });

  return stats;
}

/**
 * Get invitation-specific communication statistics
 *
 * Filter criteria: Counts communications where template_type is either
 * 'invitation' or 'activation-reminder'
 *
 * deliveryRate calculation: ((totalSent - totalFailed) / totalSent) * 100
 *
 * @returns Invitation statistics with 0-100 percentage delivery rate
 */
export async function getInvitationStats(): Promise<InvitationStats> {
  const { data: rows, error } = await supabase
    .from('communications')
    .select('status, template_snapshot')
    .eq('entity_type', 'user')
    .in('template_snapshot->template_type', ['invitation', 'activation-reminder']);

  if (error) {
    throw new Error(`Failed to get invitation stats: ${error.message}`);
  }

  const stats = (rows || []).reduce((acc, row) => {
    const snapshot = row.template_snapshot as RawTemplateSnapshot | null;
    const templateType = snapshot?.template_type;
    const isSuccessful = row.status === 'sent' || row.status === 'delivered';

    if (templateType === 'invitation') {
      acc.totalInvitationsSent++;
      if (!isSuccessful) acc.failedInvitations++;
    } else if (templateType === 'activation-reminder') {
      acc.totalRemindersSent++;
      if (!isSuccessful) acc.failedReminders++;
    }

    return acc;
  }, {
    totalInvitationsSent: 0,
    totalRemindersSent: 0,
    failedInvitations: 0,
    failedReminders: 0
  });

  const totalSent = stats.totalInvitationsSent + stats.totalRemindersSent;
  const totalFailed = stats.failedInvitations + stats.failedReminders;

  return {
    ...stats,
    deliveryRate: totalSent > 0 ? Math.round(((totalSent - totalFailed) / totalSent) * 100) : 0
  };
}
